import { useState } from "react";

// WaterWise's own brand colors (see lib/core/theme/app_colors.dart) -
// blue for water, green for movement - rather than reusing a borrowed
// accent, so the permission cards below read as "this app" at a glance.
const palette = {
    bg: "#0A0E14",
    surface: "#121826",
    raised: "#182032",
    border: "#202B40",
    accent: "#4FA8E0",       // water blue
    accentDim: "#0E2233",
    accent2: "#5FCB7A",      // movement green
    accent2Dim: "#0E2416",
    text: "#EAEFF7",
    muted: "#7C8AA3",
    faint: "#3B4760",
    success: "#22C97A",
    successDim: "#0A2218",
    danger: "#FF5C7A",
    dangerDim: "#2A0E16",
    warning: "#FFB547",
    warningDim: "#251A06",
    info: "#8FA9FF",
    infoDim: "#131A33",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }

  .ww-pp {
    background: ${palette.bg};
    color: ${palette.text};
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    line-height: 1.7;
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
  }

  /* faint droplet-ripple backdrop - concentric rings echo water rings,
     not just a generic decorative texture */
  .ww-rings-bg {
    position: fixed;
    inset: 0;
    background-image:
      repeating-radial-gradient(
        circle at 50% -10%,
        ${palette.border}26 0px,
        ${palette.border}26 1px,
        transparent 1px,
        transparent 90px
      );
    pointer-events: none;
    z-index: 0;
    opacity: 0.6;
  }

  .ww-glow {
    position: fixed;
    top: -120px;
    left: 50%;
    transform: translateX(-50%);
    width: 640px;
    height: 420px;
    background: radial-gradient(ellipse, ${palette.accent}18 0%, transparent 65%);
    pointer-events: none;
    z-index: 0;
    animation: ww-pulse 5s ease-in-out infinite alternate;
  }

  @keyframes ww-pulse {
    from { opacity: 0.55; }
    to   { opacity: 1; }
  }

  .ww-wrap {
    position: relative;
    z-index: 1;
    max-width: 760px;
    margin: 0 auto;
    padding: 0 20px 80px;
  }

  /* Hero */
  .ww-hero {
    text-align: center;
    padding: 56px 0 44px;
    animation: ww-fadeup .6s ease both;
  }

  .ww-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}44;
    border-radius: 100px;
    padding: 5px 14px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.5px;
    color: ${palette.accent};
    text-transform: uppercase;
    margin-bottom: 20px;
  }

  .ww-hero h1 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(30px, 6vw, 48px);
    font-weight: 700;
    letter-spacing: -1px;
    line-height: 1.1;
    margin-bottom: 12px;
  }

  .ww-hero h1 em { color: ${palette.accent}; font-style: normal; }

  .ww-hero-sub {
    color: ${palette.muted};
    font-size: 14px;
    max-width: 460px;
    margin: 0 auto 18px;
  }

  .ww-meta {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px 20px;
    color: ${palette.faint};
    font-size: 12px;
  }

  .ww-meta span {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  /* Divider */
  .ww-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, ${palette.border}, transparent);
    margin: 36px 0;
  }

  /* Section */
  .ww-section {
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 16px;
    padding: 24px 26px 20px;
    margin-bottom: 12px;
    transition: border-color .2s;
    animation: ww-fadeup .5s ease both;
  }

  .ww-section:hover { border-color: ${palette.accent}30; }

  .ww-section-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
  }

  .ww-icon {
    width: 36px; height: 36px; min-width: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
  }

  .ww-section h2 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -.1px;
  }

  .ww-section p, .ww-section li {
    color: ${palette.muted};
    font-size: 14px;
    line-height: 1.75;
  }

  .ww-section ul {
    padding-left: 18px;
    margin-top: 8px;
  }

  .ww-section li { margin-bottom: 5px; }

  .ww-section p + p { margin-top: 10px; }

  .ww-section a {
    color: ${palette.info};
    text-decoration: none;
    border-bottom: 1px solid ${palette.info}44;
    transition: border-color .15s;
  }

  .ww-section a:hover { border-color: ${palette.info}; }

  /* callout boxes */
  .ww-box {
    border-radius: 10px;
    padding: 11px 15px;
    margin-top: 14px;
    font-size: 13px;
    display: flex;
    gap: 9px;
    align-items: flex-start;
    line-height: 1.6;
  }

  .ww-box-success {
    background: ${palette.successDim};
    border: 1px solid ${palette.success}33;
    color: ${palette.success}CC;
  }

  .ww-box-warning {
    background: ${palette.warningDim};
    border: 1px solid ${palette.warning}33;
    color: ${palette.warning}CC;
  }

  /* Grid cards */
  .ww-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin: 14px 0 4px;
  }

  @media (max-width: 520px) { .ww-grid { grid-template-columns: 1fr; } }

  .ww-card {
    background: ${palette.raised};
    border: 1px solid ${palette.border};
    border-radius: 12px;
    padding: 13px 15px;
    display: flex;
    align-items: flex-start;
    gap: 11px;
  }

  .ww-card-icon {
    width: 32px; height: 32px; min-width: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .ww-card strong {
    font-size: 13px;
    font-weight: 600;
    color: ${palette.text};
    display: block;
    margin-bottom: 3px;
  }

  .ww-card p {
    font-size: 12px;
    color: ${palette.muted};
    line-height: 1.5;
    margin: 0 !important;
  }

  /* Contact */
  .ww-contact {
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}33;
    border-radius: 16px;
    padding: 32px;
    text-align: center;
    margin-top: 36px;
  }

  .ww-contact h3 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .ww-contact p {
    color: ${palette.muted};
    font-size: 14px;
    margin-bottom: 20px;
  }

  .ww-contact-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: ${palette.accent};
    color: #0A0E14;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 14px;
    padding: 12px 24px;
    border-radius: 100px;
    text-decoration: none;
    transition: opacity .2s, transform .2s;
  }

  .ww-contact-btn:hover { opacity: .87; transform: translateY(-1px); }

  /* Footer */
  .ww-footer {
    text-align: center;
    padding-top: 28px;
    color: ${palette.faint};
    font-size: 12px;
  }

  @keyframes ww-fadeup {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

// ── Icon helpers ──────────────────────────────────────────────────────────
const Ico = ({ e }) => <span role="img" aria-hidden="true">{e}</span>;

function SecIcon({ bg, children }) {
    return <div className="ww-icon" style={{ background: bg }}>{children}</div>;
}

function Section({ iconBg, icon, title, children, delay = 0 }) {
    return (
        <div className="ww-section" style={{ animationDelay: `${delay}s` }}>
            <div className="ww-section-head">
                <SecIcon bg={iconBg}><Ico e={icon} /></SecIcon>
                <h2>{title}</h2>
            </div>
            {children}
        </div>
    );
}

function SuccessBox({ children }) {
    return <div className="ww-box ww-box-success"><Ico e="✓" /><span>{children}</span></div>;
}

function WarnBox({ children }) {
    return <div className="ww-box ww-box-warning"><Ico e="⚠" /><span>{children}</span></div>;
}

function InfoCard({ icon, iconBg, title, desc }) {
    return (
        <div className="ww-card">
            <div className="ww-card-icon" style={{ background: iconBg }}>
                <Ico e={icon} />
            </div>
            <div>
                <strong>{title}</strong>
                <p>{desc}</p>
            </div>
        </div>
    );
}

// ── Main ──────────────────────────────────────────────────────────────────
// Replace the placeholder developer name and email below with your own
// before publishing - they're left as clearly-marked placeholders rather
// than guessed values.
const DEVELOPER_NAME = "AlphaY";
const CONTACT_EMAIL = "alphay28071994@gmail.com";

export default function WaterWisePrivacyPolicy() {
    const [year] = useState(new Date().getFullYear());

    return (
        <>
            <style>{css}</style>
            <div className="ww-pp">
                <div className="ww-rings-bg" />
                <div className="ww-glow" />

                <div className="ww-wrap">

                    {/* Hero */}
                    <header className="ww-hero">
                        <h1>Privacy <em>Policy</em></h1>
                        <p className="ww-hero-sub">
                            How <strong>WaterWise</strong> handles the permissions it asks for, and — just as
                            importantly — everything it doesn't do with them.
                        </p>
                        <div className="ww-meta">
                            <span>🧭 Developer: {DEVELOPER_NAME}</span>
                            <span>📱 Free app · No account</span>
                        </div>
                    </header>

                    <div className="ww-divider" />

                    {/* Overview */}
                    <Section iconBg={palette.accentDim} icon="📄" title="Overview" delay={0.05}>
                        <p>
                            <strong style={{ color: palette.text }}>WaterWise</strong> is a free hydration and
                            movement reminder app for Android. It nudges you to drink water and take short
                            movement breaks during your working hours, and tracks your streaks and history along
                            the way. This page explains what WaterWise can access on your device, why, and what
                            it does with it.
                        </p>
                        <p>By installing and using WaterWise, you agree to the practices described below.</p>
                        <SuccessBox>
                            WaterWise has no account, no sign-in, no ads, and no analytics or tracking SDKs.
                            Everything it stores is kept on your device — nothing is sent to us or anyone else.
                        </SuccessBox>
                    </Section>

                    {/* Permissions */}
                    <Section iconBg={palette.infoDim} icon="🔑" title="Permissions WaterWise uses" delay={0.10}>
                        <p>
                            WaterWise's core feature is scheduling full-screen reminders that fire even if the
                            app is closed, so it needs a handful of permissions most apps wouldn't. All of them
                            exist purely to make sure a reminder reaches you reliably and on time — none are
                            used to read, share, or transmit anything about you.
                        </p>
                        <div className="ww-grid">
                            <InfoCard
                                icon="🔔" iconBg={palette.warningDim}
                                title="Notifications"
                                desc="Lets WaterWise show you water and movement reminders at all. Every notification is scheduled locally on your device — WaterWise never sends push notifications from a server."
                            />
                            <InfoCard
                                icon="🖥️" iconBg={palette.accentDim}
                                title="Full-screen alerts"
                                desc="Lets a reminder open as a full-screen prompt (like an alarm) instead of a quiet tray notification, so it's harder to miss during a focused work session."
                            />
                            <InfoCard
                                icon="⏱️" iconBg={palette.accent2Dim}
                                title="Exact alarms"
                                desc="Keeps reminders firing at the precise interval you set, rather than being delayed or batched by the OS's default power-saving behavior."
                            />
                            <InfoCard
                                icon="🔆" iconBg={palette.infoDim}
                                title="Wake screen"
                                desc="Briefly wakes your screen so a full-screen reminder is actually visible when it fires, the same way an alarm clock app would."
                            />
                            <InfoCard
                                icon="📳" iconBg="#1A1030"
                                title="Vibration"
                                desc="Vibrates alongside the reminder sound. Purely local device feedback — no data involved."
                            />
                            <InfoCard
                                icon="🔁" iconBg={palette.successDim}
                                title="Run at startup"
                                desc="Lets WaterWise re-arm your reminder schedule after your phone restarts, so a reboot doesn't silently turn reminders off."
                            />
                        </div>
                        <WarnBox>
                            WaterWise's Android package also declares standard internet access, which several of
                            its underlying libraries include by default. WaterWise itself never uses it to send
                            your data anywhere — there is no server for it to send data to.
                        </WarnBox>
                    </Section>

                    {/* Data Collected */}
                    <Section iconBg={palette.successDim} icon="🗂️" title="Information we collect" delay={0.15}>
                        <p>
                            <strong style={{ color: palette.text }}>We do not collect personally identifiable information.</strong>{" "}
                            WaterWise does not require an account, name, email, or any profile data to use.
                        </p>
                        <p>
                            Your schedule, water intake log, movement log, streaks, and reminder history are all
                            stored <strong style={{ color: palette.text }}>locally on your device only</strong>,
                            in a local database and local preferences store. None of it is transmitted to any
                            server, because WaterWise doesn't have one.
                        </p>
                        <SuccessBox>
                            Uninstalling WaterWise permanently deletes all of this data from your device.
                        </SuccessBox>
                    </Section>

                    {/* Third Parties */}
                    <Section iconBg={palette.warningDim} icon="🔌" title="Third-party services" delay={0.20}>
                        <p>
                            WaterWise keeps its list of outside dependencies short on purpose — there is no
                            advertising network and no analytics SDK anywhere in the app.
                        </p>
                        <div className="ww-grid">
                            <InfoCard
                                icon="▶️" iconBg="#0A1A10"
                                title="Google Play Services"
                                desc="Used by Android itself for app distribution and updates. WaterWise doesn't add any tracking on top of it."
                            />
                            <InfoCard
                                icon="💾" iconBg={palette.successDim}
                                title="On-device storage"
                                desc="Your logs, streaks, and settings are kept in local storage only. Your data never leaves your phone."
                            />
                        </div>
                        <WarnBox>
                            WaterWise does not use Google AdMob, Firebase Analytics, Crashlytics, or any similar
                            service. If that ever changes, this policy will be updated first.
                        </WarnBox>
                    </Section>

                    {/* Data Retention */}
                    <Section iconBg="#0A1525" icon="🕓" title="Data retention" delay={0.25}>
                        <p>
                            Everything WaterWise stores — your schedule, intake and movement logs, streaks, and
                            reminder history — stays on your device until you clear the app's storage or
                            uninstall it, at which point Android deletes it automatically.
                        </p>
                        <p>
                            We do not maintain any server-side database of your data, because none of it is
                            ever sent to us in the first place.
                        </p>
                    </Section>

                    {/* Children */}
                    <Section iconBg={palette.dangerDim} icon="🧒" title="Children's privacy" delay={0.30}>
                        <p>
                            WaterWise is a general-audience health and productivity tool and is not directed at
                            children. We do not knowingly collect personal information from children under 13
                            (or under 16 in the EU/EEA) — in practice, this is straightforward, since WaterWise
                            doesn't collect personal information from anyone, of any age.
                        </p>
                        <p>
                            If you believe a child has been affected in a way this policy doesn't account for,
                            please contact us at{" "}
                            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we'll look into it
                            promptly.
                        </p>
                    </Section>

                    {/* Security */}
                    <Section iconBg={palette.successDim} icon="🛡️" title="Security" delay={0.35}>
                        <p>
                            Since WaterWise doesn't transmit your personal data anywhere, your main security
                            consideration is your device itself. We recommend keeping Android and WaterWise
                            updated, and being deliberate about which permissions you grant it — including the
                            ones described above.
                        </p>
                    </Section>

                    {/* Changes */}
                    <Section iconBg={palette.warningDim} icon="🔄" title="Changes to this policy" delay={0.40}>
                        <p>
                            We may update this privacy policy from time to time. Changes will be posted on this
                            page with an updated effective date at the top.
                        </p>
                        <p>
                            We encourage you to review this policy periodically. Continued use of WaterWise
                            after changes are posted constitutes acceptance of the updated policy.
                        </p>
                    </Section>

                    {/* Consent */}
                    <Section iconBg={palette.successDim} icon="✅" title="Your consent" delay={0.45}>
                        <p>
                            By installing and using WaterWise, you consent to this privacy policy. If you do not
                            agree, please discontinue use of the app, turn off any permissions you granted it in
                            Android Settings, and uninstall it.
                        </p>
                    </Section>

                    {/* Contact */}
                    <div className="ww-contact">
                        <h3>Questions or concerns?</h3>
                        <p>
                            If anything here isn't clear, or you want to know more about how a specific
                            permission is used, reach out any time.
                        </p>
                        <a href={`mailto:${CONTACT_EMAIL}`} className="ww-contact-btn">
                            ✉️ &nbsp;{CONTACT_EMAIL}
                        </a>
                    </div>

                    {/* Footer */}
                    <div className="ww-divider" />
                    <footer className="ww-footer">
                        <p>© {year} <strong style={{ color: palette.muted }}>{DEVELOPER_NAME}</strong> · WaterWise · All rights reserved.</p>
                        <p style={{ marginTop: 5 }}>This app is not affiliated with or endorsed by Google LLC.</p>
                    </footer>

                </div>
            </div>
        </>
    );
}